import api from '../httpClient';
import {
  type AuthRecoverySnapshot,
  authRecoverySnapshotRequiresHandshake,
  assertAuthRecoverySnapshotCurrent,
  captureAuthRecoverySnapshot,
  completeAmbiguousLogoutRecovery,
  getAuthRevocationSafetyError,
  requiresAmbiguousLogoutRecovery,
  startBoundedAuthRevocation,
  waitForPendingAuthRevocations,
} from '../../utils/authTransitions';

const completeRecoveryHandshake = <T extends { data?: unknown }>(
  response: T,
  recoverySnapshot: AuthRecoverySnapshot,
): T & { authRecoverySnapshot: AuthRecoverySnapshot } => {
  const recoveryRequested = authRecoverySnapshotRequiresHandshake(recoverySnapshot);
  if (!recoveryRequested) {
    return Object.assign(response, { authRecoverySnapshot: recoverySnapshot });
  }

  const recoveryPerformed = Boolean(
    response.data &&
    typeof response.data === 'object' &&
    (response.data as { auth_recovery_performed?: unknown }).auth_recovery_performed === true,
  );
  if (!recoveryPerformed) {
    throw getAuthRevocationSafetyError();
  }
  return Object.assign(response, { authRecoverySnapshot: recoverySnapshot });
};

export const auth = {
  login: async (usernameOrEmail: string, password: string) => {
    await waitForPendingAuthRevocations();
    const recoverySnapshot = captureAuthRecoverySnapshot();
    const recoveryRequested = authRecoverySnapshotRequiresHandshake(recoverySnapshot);
    const response = await api.post('/api/token/', {
      username: usernameOrEmail,
      password,
      ...(recoveryRequested ? { recover_ambiguous_logout: true } : {}),
    });
    return completeRecoveryHandshake(response, recoverySnapshot);
  },
  register: (
    firstName: string,
    lastName: string,
    password: string,
    email: string,
    phoneNumber: string,
    signupCode: string,
  ) =>
    api.post('/api/register/', {
      first_name: firstName,
      last_name: lastName,
      password,
      email,
      phone_number: phoneNumber,
      signup_code: signupCode,
    }),
  validateSignupCode: (signupCode: string, email?: string) =>
    api.post('/api/signup-codes/validate/', { signup_code: signupCode, email }),
  // Legacy alias retained for compatibility with unused screens during cutover.
  validateOrganization: (orgId: string) =>
    api.post('/api/signup-codes/validate/', { signup_code: orgId }),
  getOrganizations: () =>
    api.get('/api/organizations/').then((response) => response.data),
  requestPasswordReset: (email: string) =>
    api.post('/api/auth/forgot-password/', { email }),
  validateResetToken: (token: string) =>
    api.get('/api/auth/reset-password/', { params: { token } }),
  resetPassword: ({ token, newPassword }: { token: string; newPassword: string }) =>
    api.post('/api/auth/reset-password/', {
      token,
      new_password: newPassword,
    }),
  logout: () => startBoundedAuthRevocation((signal) => (
    api.post('/api/auth/logout/', undefined, { signal })
  )),
  waitForPendingLogout: () => waitForPendingAuthRevocations(),
  requiresLogoutRecovery: () => requiresAmbiguousLogoutRecovery(),
  captureLogoutRecoverySnapshot: () => captureAuthRecoverySnapshot(),
  assertLogoutRecoverySnapshotCurrent: (snapshot: AuthRecoverySnapshot) => (
    assertAuthRecoverySnapshotCurrent(snapshot)
  ),
  completeLogoutRecovery: (snapshot: AuthRecoverySnapshot) => (
    completeAmbiguousLogoutRecovery(snapshot)
  ),
  startNativeGoogle: (codeChallenge: string, recoveryRequested: boolean, signal: AbortSignal, appId: string, signupCode?: string) => (
    api.post<{ native_request: string; expires_in: number }>('/api/auth/google/native/start/', {
      code_challenge: codeChallenge,
      app_id: appId,
      ...(signupCode ? { purpose: 'signup', signup_code: signupCode } : { purpose: 'login' }),
      ...(recoveryRequested ? { recover_ambiguous_logout: true } : {}),
    }, { signal })
  ),
  exchangeNativeGoogle: async (requestId: string, verifier: string, completion: string, recoverySnapshot: AuthRecoverySnapshot, signal: AbortSignal) => {
    assertAuthRecoverySnapshotCurrent(recoverySnapshot);
    const response = await api.post('/api/auth/google/native/exchange/', {
      native_request: requestId,
      code_verifier: verifier,
      completion,
      ...(authRecoverySnapshotRequiresHandshake(recoverySnapshot) ? { recover_ambiguous_logout: true } : {}),
    }, { signal });
    return response.status === 202 ? null : completeRecoveryHandshake(response, recoverySnapshot);
  },
  cancelNativeGoogle: (requestId: string, verifier: string) => (
    api.delete('/api/auth/google/native/exchange/', {
      data: { native_request: requestId, code_verifier: verifier }, timeout: 5000,
    })
  ),
  exchangeOAuthCode: async (code: string) => {
    await waitForPendingAuthRevocations();
    const recoverySnapshot = captureAuthRecoverySnapshot();
    const recoveryRequested = authRecoverySnapshotRequiresHandshake(recoverySnapshot);
    const response = await api.post('/api/auth/google/exchange/', {
      code,
      ...(recoveryRequested ? { recover_ambiguous_logout: true } : {}),
    });
    return completeRecoveryHandshake(response, recoverySnapshot);
  },
};
